import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './Header';
import Sidebar from './Sidebar';
import ErrorBoundary from './ui/ErrorBoundary';
import QuickInputIntegration from './QuickInputIntegration';
import { useAppStore } from '@/store';
import type { Notification } from '@/types';

const Layout: React.FC = () => {
  const { sidebarOpen, notifications, theme, markNotificationAsRead } = useAppStore();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const handleNotificationClick = (notification: Notification) => {
    // Помечаем уведомление как прочитанное
    markNotificationAsRead(notification.id);
    
    // Переходим к странице уведомлений
    navigate('/notifications');
  };

  return (
    <ErrorBoundary>
      <QuickInputIntegration>
        <div className={`h-screen flex overflow-hidden ${theme === 'dark' ? 'dark' : ''}`}>
          {/* Mobile sidebar overlay */}
          {mobileSidebarOpen && (
            <div
              className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          {/* Sidebar for mobile - hidden by default, slides in when opened */}
          <aside className="lg:hidden">
            <div
              className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out ${
                mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <Sidebar mobile={true} onClose={() => setMobileSidebarOpen(false)} />
            </div>
          </aside>

          {/* Sidebar for desktop - always visible */}
          <aside className="hidden lg:flex lg:flex-shrink-0">
            <div className="flex flex-col w-64">
              <Sidebar />
            </div>
          </aside>

          {/* Main content */}
          <div className="flex flex-col w-0 flex-1 overflow-hidden">
            {/* Header with hamburger button */}
            <Header onMenuClick={() => setMobileSidebarOpen(true)} />

            {/* Page content */}
            <main className="flex-1 relative overflow-y-auto focus:outline-none">
              <div className="py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                  <Outlet />
                </div>
              </div>
            </main>
          </div>

          {/* Toast notifications */}
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: theme === 'dark' ? '#374151' : '#ffffff',
                color: theme === 'dark' ? '#f9fafb' : '#111827',
                border: theme === 'dark' ? '1px solid #4b5563' : '1px solid #e5e7eb',
                zIndex: 40,
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />

          {/* In-app notifications */}
          {notifications.filter(n => !n.read).length > 0 && (
            <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
              {notifications.filter(n => !n.read).slice(0, 3).map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 rounded-lg shadow-lg border transition-all duration-300 transform cursor-pointer hover:scale-105 ${
                    notification.type === 'success' 
                      ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-900 dark:text-green-100' :
                    notification.type === 'warning' 
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100' :
                    notification.type === 'error' 
                      ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-900 dark:text-red-100' :
                      'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-100'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {notification.type === 'success' && <span className="text-green-400">✅</span>}
                      {notification.type === 'warning' && <span className="text-yellow-400">⚠️</span>}
                      {notification.type === 'error' && <span className="text-red-400">❌</span>}
                      {notification.type === 'info' && <span className="text-blue-400">ℹ️</span>}
                    </div>
                    <div className="ml-3 w-0 flex-1">
                      <p className="text-sm font-medium">
                        {notification.title}
                      </p>
                      <p className="mt-1 text-sm">
                        {notification.message}
                      </p>
                      {notification.action && (
                        <div className="mt-3">
                          <button
                            onClick={notification.action.handler}
                            className="text-sm font-medium underline hover:no-underline"
                          >
                            {notification.action.label}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0 flex">
                      <button
                        onClick={() => {
                          useAppStore.getState().markNotificationAsRead(notification.id);
                        }}
                        className="text-sm text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </QuickInputIntegration>
    </ErrorBoundary>
  );
};

export default Layout;